import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ref, onValue } from 'firebase/database';
import { db } from '../firebaseConfig';
import Navbar from '../components/Navbar';
import LocationPicker from '../components/LocationPicker';
import MapPreview from '../components/MapPreview';
import { useLanguage } from '../LanguageContext';

export default function HomePage() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [user, setUser] = useState(null);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [fromCoord, setFromCoord] = useState(null);
  const [toCoord, setToCoord] = useState(null);
  const [showRoute, setShowRoute] = useState(false);
  const [allRides, setAllRides] = useState([]);
  const [connected, setConnected] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [dateOption, setDateOption] = useState('today');
  const [sortBy, setSortBy] = useState(null);

  useEffect(() => {
    const savedUser = localStorage.getItem('guningo_user');
    if (!savedUser) {
      navigate('/');
      return;
    }
    setUser(JSON.parse(savedUser));
  }, [navigate]);

  useEffect(() => {
    const ridesRef = ref(db, 'rides/');
    const unsubscribe = onValue(ridesRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const ridesArray = Object.keys(data).map(key => ({ id: key, ...data[key] }));
        setAllRides(ridesArray);
        setConnected(true);
      } else {
        setAllRides([]);
        setConnected(true);
      }
    });
    return () => unsubscribe();
  }, []);

  const formatDate = (date) => {
    const day = date.getDate().toString().padStart(2, '0');
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    const weekdays = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
    return weekdays[date.getDay()] + ' ' + day + ' ' + months[date.getMonth()];
  };

  const toInputDate = (date) => {
    const y = date.getFullYear();
    const m = (date.getMonth() + 1).toString().padStart(2, '0');
    const d = date.getDate().toString().padStart(2, '0');
    return y + '-' + m + '-' + d;
  };

  const selectToday = () => { setSelectedDate(new Date()); setDateOption('today'); };
  const selectTomorrow = () => {
    const tmr = new Date();
    tmr.setDate(tmr.getDate() + 1);
    setSelectedDate(tmr);
    setDateOption('tomorrow');
  };

  const handleFromSelect = (place) => {
    setFrom(place.cityName);
    setFromCoord({ latitude: place.latitude, longitude: place.longitude });
    setShowRoute(false);
  };

  const handleToSelect = (place) => {
    setTo(place.cityName);
    setToCoord({ latitude: place.latitude, longitude: place.longitude });
    setShowRoute(false);
  };

  const selectedDateStr = formatDate(selectedDate);
  let rides = allRides.filter(r => {
    const dateMatches = r.date === selectedDateStr && r.status !== 'cancelled_by_driver';
   const fromMatches = !from.trim() || (r.from || '').toLowerCase().includes(from.trim().toLowerCase());
const toMatches = !to.trim() || (r.to || '').toLowerCase().includes(to.trim().toLowerCase());
    return dateMatches && fromMatches && toMatches;
  });

  if (sortBy === 'price') {
    rides = rides.slice().sort((a, b) => (a.price || 0) - (b.price || 0));
  } else if (sortBy === 'time') {
    rides = rides.slice().sort((a, b) => (a.time || '').localeCompare(b.time || ''));
  }

  if (!user) return null;

  return (
    <div style={styles.page}>
      <Navbar userName={user.name} userPhone={user.phone} />

      <div style={styles.hero} className="hero-section">
        <div style={styles.heroLeft}>
          <h1 style={styles.headline}>{t('shareRides')}</h1>

          <div style={styles.searchCard}>
            <div style={styles.searchField}>
              <p style={styles.fieldLabel}>{t('from')}</p>
              <LocationPicker placeholder="Leaving from..." value={from} onSelect={handleFromSelect} />
            </div>
            <div style={styles.searchField}>
              <p style={styles.fieldLabel}>{t('to')}</p>
              <LocationPicker placeholder="Going to..." value={to} onSelect={handleToSelect} />
            </div>

            <div style={styles.dateRow}>
              <button style={dateOption === 'today' ? styles.dateChipActive : styles.dateChip} onClick={selectToday}>{t('today')}</button>
              <button style={dateOption === 'tomorrow' ? styles.dateChipActive : styles.dateChip} onClick={selectTomorrow}>{t('tomorrow')}</button>
              <div style={styles.dateInputWrap}>
                <input
                  type="date"
                  lang="en-GB"
                  value={dateOption === 'custom' ? toInputDate(selectedDate) : ''}
                  min={toInputDate(new Date())}
                  onChange={(e) => {
                    if (!e.target.value) return;
                    const [y, m, d] = e.target.value.split('-').map(Number);
                    const picked = new Date(y, m - 1, d);
                    setSelectedDate(picked);
                    setDateOption('custom');
                  }}
                  style={dateOption === 'custom' ? styles.dateInputActive : styles.dateInput}
                />
                {dateOption !== 'custom' && (
                  <span style={styles.dateInputLabel}>Choose date</span>
                )}
              </div>
            </div>
            {dateOption === 'custom' && (
              <p style={styles.customDateNote}>Showing rides for {selectedDateStr}</p>
            )}

            <div style={styles.sortRow}>
              <button style={sortBy === 'price' ? styles.sortChipActive : styles.sortChip} onClick={() => setSortBy(sortBy === 'price' ? null : 'price')}>{t('sortByPrice')}</button>
              <button style={sortBy === 'time' ? styles.sortChipActive : styles.sortChip} onClick={() => setSortBy(sortBy === 'time' ? null : 'time')}>{t('sortByTime')}</button>
            </div>
          </div>

          <div style={styles.statusRow}>
            <span style={{ ...styles.statusDot, background: connected ? '#00c896' : '#f5a623' }}></span>
            <span style={styles.statusText}>{connected ? 'Live — rides updating' : 'Connecting...'}</span>
          </div>
        </div>

        <div style={styles.heroRight}>
          {(fromCoord || toCoord) ? (
            <div style={{ width: '100%', maxWidth: 480 }}>
              <div style={styles.mapWrap}>
                <MapPreview fromCoord={fromCoord} toCoord={toCoord} showRoute={showRoute} />
              </div>
              {fromCoord && toCoord && (
                <button style={styles.routeBtn} onClick={() => setShowRoute(!showRoute)}>
                  {showRoute ? '✕ Hide Route' : '🗺️ Show Route'}
                </button>
              )}
            </div>
          ) : (
            <svg width="100%" viewBox="0 0 260 160" style={{ maxWidth: 300 }}>
              <ellipse cx="130" cy="145" rx="100" ry="8" fill="#c7dff0" />
              <path d="M40 100 Q35 75 65 68 L90 45 Q110 32 140 32 L175 36 Q205 42 218 62 L235 68 Q248 74 245 92 L240 98 L40 98 Z" fill="#3a8fd0" />
              <path d="M95 48 Q115 36 140 36 L170 40 Q192 45 205 60 L100 60 Z" fill="#eaf5fc" />
              <circle cx="90" cy="105" r="7" fill="#eaf5fc" />
              <circle cx="130" cy="105" r="7" fill="#eaf5fc" />
              <circle cx="170" cy="105" r="7" fill="#eaf5fc" />
              <circle cx="75" cy="102" r="22" fill="#1a1a2e" />
              <circle cx="75" cy="102" r="9" fill="#eaf5fc" />
              <circle cx="205" cy="102" r="22" fill="#1a1a2e" />
              <circle cx="205" cy="102" r="9" fill="#eaf5fc" />
            </svg>
          )}
        </div>
      </div>

      <div style={styles.listSection}>
        <h3 style={styles.sectionTitle}>{rides.length} {t('ridesPosted')}</h3>

        {rides.length === 0 ? (
          <div style={styles.emptyState}>
            <div style={styles.emptyIcon}>🚗</div>
            <h3 style={styles.emptyTitle}>{t('noRidesFound')}</h3>
            <p style={styles.emptyText}>Try a different date or route</p>
          </div>
        ) : (
          <div style={styles.ridesGrid}>
            {rides.map(ride => (
              <div key={ride.id} style={styles.rideCard}>
             <div style={styles.rideTop}>
  <div style={styles.avatar}>{ride.initials || '??'}</div>
  <div style={styles.rideInfo}>
    <div style={styles.rideRoute}>{ride.from} → {ride.to}</div>
  </div>
  <div style={styles.priceBox}>
    <div style={styles.price}>₹{ride.price}</div>
    <div style={styles.priceLabel}>{t('perSeat')}</div>
  </div>
</div>

{ride.fromAddress && ride.fromAddress !== ride.from && (
  <div style={styles.addressLine}>📍 {ride.fromAddress}</div>
)}
{ride.toAddress && ride.toAddress !== ride.to && (
  <div style={styles.addressLine}>🏁 {ride.toAddress}</div>
)}

<div style={styles.driverRow}>
  <span style={styles.ratingText}>⭐ {ride.rating || '5.0'}</span>
  <span style={styles.driverName}>{ride.driver}</span>
</div>
<div style={styles.rideMeta}>📅 {ride.date} ⏰ {ride.time}</div>
<div style={styles.rideMeta}>🚗 {ride.vehicle}</div>
                <div style={styles.badgeRow}>
                  <span style={styles.badge}>{ride.seats} {t('seatsLeft')}</span>
                </div>
                <button
                  style={styles.bookBtn}
                  onClick={() => navigate(
                    '/booking?rideId=' + ride.id +
                    '&from=' + encodeURIComponent(ride.from) +
                    '&to=' + encodeURIComponent(ride.to) +
                    '&date=' + encodeURIComponent(ride.date) +
                    '&time=' + encodeURIComponent(ride.time) +
                    '&driver=' + encodeURIComponent(ride.driver) +
                    '&driverPhone=' + encodeURIComponent(ride.driverPhone || '') +
                    '&price=' + ride.price +
                    '&seatsAvailable=' + ride.seats +
                    '&vehicle=' + encodeURIComponent(ride.vehicle) +
                    '&rating=' + encodeURIComponent(ride.rating || '5.0')
                  )}
                >{t('bookThisRide')} →</button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  page: { minHeight: '100vh', background: '#ffffff', fontFamily: 'Arial, sans-serif' },
  hero: { background: '#eaf5fc', padding: '50px 40px', display: 'flex', alignItems: 'center', gap: 40, flexWrap: 'wrap' },
  heroLeft: { flex: 1, minWidth: 300, maxWidth: 460 },
  heroRight: { flex: 1.2, minWidth: 340, display: 'flex', justifyContent: 'center', alignItems: 'flex-start' },
  headline: { fontSize: 30, fontWeight: 800, color: '#1a1a2e', lineHeight: 1.3, margin: '0 0 24px' },
  searchCard: { background: '#ffffff', border: '1.5px solid #3a8fd0', borderRadius: 16, padding: 22, boxShadow: '0 8px 24px rgba(58,143,208,0.12)', marginBottom: 14 },
  searchField: { borderBottom: '1px solid #eef1f8', paddingBottom: 12, marginBottom: 12 },
  fieldLabel: { fontSize: 11, color: '#9499b5', margin: '0 0 4px' },
  dateRow: { display: 'flex', gap: 8, marginBottom: 10 },
  dateChip: { flex: 1, height: 36, borderRadius: 8, border: '1.5px solid #d9edfa', background: '#eaf5fc', color: '#5a5f7d', fontWeight: 600, cursor: 'pointer', fontSize: 13 },
  dateChipActive: { flex: 1, height: 36, borderRadius: 8, border: '1.5px solid #3a8fd0', background: '#3a8fd0', color: '#ffffff', fontWeight: 600, cursor: 'pointer', fontSize: 13 },
  dateInputWrap: { flex: 1, position: 'relative' },
  dateInput: { width: '100%', height: 36, borderRadius: 8, border: '1.5px solid #d9edfa', background: '#eaf5fc', color: 'transparent', fontWeight: 600, cursor: 'pointer', fontSize: 13, padding: '0 8px', colorScheme: 'light' },
  dateInputActive: { width: '100%', height: 36, borderRadius: 8, border: '1.5px solid #3a8fd0', background: '#e6f1fb', color: '#185fa5', fontWeight: 600, cursor: 'pointer', fontSize: 13, padding: '0 8px', colorScheme: 'light' },
  dateInputLabel: { position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', fontSize: 13, fontWeight: 600, color: '#5a5f7d', pointerEvents: 'none' },
  customDateNote: { fontSize: 12, color: '#5a5f7d', margin: '0 0 10px' },
  sortRow: { display: 'flex', gap: 8 },
  sortChip: { flex: 1, height: 30, borderRadius: 8, border: '1px solid #d9edfa', background: '#f7f9fc', color: '#5a5f7d', fontWeight: 600, cursor: 'pointer', fontSize: 12 },
  sortChipActive: { flex: 1, height: 30, borderRadius: 8, border: '1px solid #3a8fd0', background: '#e6f1fb', color: '#185fa5', fontWeight: 600, cursor: 'pointer', fontSize: 12 },
  statusRow: { display: 'flex', alignItems: 'center', gap: 8 },
  statusDot: { width: 8, height: 8, borderRadius: 4, display: 'inline-block' },
  statusText: { color: '#5a5f7d', fontSize: 13 },
  mapWrap: { width: '100%', maxWidth: 480, minWidth: 320, height: 380, borderRadius: 16, overflow: 'hidden', boxShadow: '0 8px 24px rgba(0,0,0,0.1)', margin: '0 auto' },
  routeBtn: { width: '100%', maxWidth: 480, height: 44, marginTop: 12, background: '#3a8fd0', color: '#fff', border: 'none', borderRadius: 10, fontWeight: 700, cursor: 'pointer', fontSize: 14 },
  listSection: { padding: '36px 40px', maxWidth: 1100, margin: '0 auto' },
  sectionTitle: { color: '#1a1a2e', marginBottom: 16 },
  emptyState: { background: '#f7f9fc', borderRadius: 14, padding: 40, textAlign: 'center' },
  emptyIcon: { fontSize: 44, marginBottom: 10 },
  emptyTitle: { color: '#1a1a2e', margin: '0 0 4px' },
  emptyText: { color: '#9499b5', margin: 0 },
  ridesGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 },
  rideCard: { border: '1px solid #eef1f8', borderRadius: 14, padding: 16, boxShadow: '0 2px 10px rgba(0,0,0,0.04)' },
  rideTop: { display: 'flex', gap: 10, marginBottom: 10 },
  avatar: { width: 40, height: 40, borderRadius: 20, background: '#3a8fd0', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 12, flexShrink: 0 },
  rideInfo: { flex: 1 },
  rideRoute: { fontWeight: 700, color: '#1a1a2e', fontSize: 14, marginBottom: 2 },
  driverRow: { display: 'flex', gap: 6, fontSize: 12, marginBottom: 2 },
  ratingText: { color: '#c98a1a', fontWeight: 600 },
  driverName: { color: '#9499b5' },
  rideMeta: { fontSize: 12, color: '#5a5f7d' },
  priceBox: { textAlign: 'right' },
  price: { fontSize: 18, fontWeight: 800, color: '#3a8fd0' },
  addressLine: { fontSize: 11, color: '#9499b5', marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  priceLabel: { fontSize: 11, color: '#9499b5' },
  badgeRow: { marginBottom: 10 },
  badge: { background: '#f7f9fc', color: '#5a5f7d', fontSize: 11, fontWeight: 600, padding: '4px 10px', borderRadius: 6 },
  bookBtn: { width: '100%', height: 38, background: '#3a8fd0', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: 13 },
};